import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { useLang } from "../i18n";
import { track } from "@/lib/track";
import { SampleRadar } from "./assessment/charts";

const lineCls = [
  "font-normal text-white text-[1.4rem] leading-[1.15] sm:text-[2.125rem] lg:whitespace-nowrap lg:text-[2.75rem]",
  "text-gradient font-extrabold text-[2.125rem] sm:text-[3.625rem] lg:text-[4.25rem]",
];

const Hero = () => {
  const { t } = useLang();
  const lines = [
    { text: t("hero.l1", "Human immortality is theoretical."), cls: lineCls[0] },
    { text: t("hero.l2", "150 years is practical."), cls: lineCls[1] },
  ];
  return (
    <section id="top" data-testid="hero-section" className="relative overflow-hidden">
      <video
        autoPlay
        muted
        loop
        playsInline
        poster="https://images.pexels.com/videos/3141210/free-video-3141210.jpg?auto=compress&w=1600"
        data-testid="hero-background-video"
        className="pointer-events-none absolute inset-0 h-full w-full object-cover brightness-[0.32]"
      >
        <source src="https://videos.pexels.com/video-files/3141210/3141210-hd_1920_1080_25fps.mp4" type="video/mp4" />
      </video>
      <img
        src="/hero-light.png"
        alt=""
        aria-hidden="true"
        data-testid="hero-background-light"
        className="pointer-events-none absolute inset-0 hidden h-full w-full object-cover"
      />
      <div className="media-veil pointer-events-none absolute inset-0 bg-gradient-to-b from-void/80 via-void/50 to-void" />
      <div className="pointer-events-none absolute -top-48 left-1/2 h-[640px] w-[960px] -translate-x-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(139,92,246,0.16),transparent)]" />
      <div className="pointer-events-none absolute right-[-160px] top-1/3 h-[520px] w-[520px] rounded-full bg-[radial-gradient(closest-side,rgba(34,211,238,0.10),transparent)]" />

      <div className="relative z-10 mx-auto flex w-full max-w-6xl flex-col items-center px-6 pb-28 pt-32 text-center md:pt-36">
        <motion.div
          data-testid="hero-eyebrow"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="badge-animated flex items-center gap-3 rounded-full px-4 py-3 backdrop-blur sm:px-6"
        >
          <span className="text-base font-medium text-white/85">
            {t("hero.badge", "Engineering Longevity")}
          </span>
        </motion.div>

        <h1 className="mt-12 font-hero leading-[1.08] tracking-[-0.01em]">
          {lines.map((line, i) => (
            <span key={line.text} className="block overflow-hidden pb-1">
              <motion.span
                data-testid={`hero-line-${i + 1}`}
                className={`block ${line.cls}`}
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.1, delay: 0.4 + i * 0.2, ease: [0.22, 1, 0.36, 1] }}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-subtext"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.0, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 max-w-2xl text-lg font-light leading-relaxed text-fog md:text-xl"
        >
          {t("hero.sub", "Longevity is a multidisciplinary human challenge. Medicine & Health Sciences have driven remarkable advances in longevity. The next breakthroughs may come from their convergence with engineering, AI, neurology, psychology, cognitive science, quantum mechanics, particle physics and space-time.")}
        </motion.p>
        <motion.p
          data-testid="hero-subtext-2"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.12, ease: [0.22, 1, 0.36, 1] }}
          className="mt-4 max-w-2xl text-lg font-light leading-relaxed text-fog md:text-xl"
        >
          {t("hero.sub2", "The Lifespan Project brings these disciplines together. Nothing in the known laws of physics appears to forbid a far longer human life. We intend to investigate and prove what is possible.")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex w-full flex-col items-center justify-center gap-4 sm:w-auto sm:flex-row"
        >
          <Link
            to="/subscribe"
            data-testid="hero-back-project-button"
            className="group flex w-full items-center justify-center gap-3 rounded-full bg-gradient-green px-6 py-3.5 text-base font-semibold text-black shadow-[0_8px_32px_rgba(74,222,128,0.30)] transition-shadow duration-300 hover:shadow-[0_8px_44px_rgba(163,230,53,0.45)] sm:w-auto sm:px-8 sm:py-4"
          >
            {t("hero.cta1", "Subscribe the Project")}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <a
            href="#thesis"
            data-testid="hero-read-thesis-button"
            className="flex w-full items-center justify-center gap-3 rounded-full bg-gradient-accent px-6 py-3.5 text-base font-semibold text-white shadow-[0_8px_32px_rgba(59,130,246,0.35)] transition-shadow duration-300 hover:shadow-[0_8px_44px_rgba(34,211,238,0.5)] sm:w-auto sm:px-8 sm:py-4"
          >
            {t("hero.cta2", "Read the Thesis")}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.3, ease: [0.22, 1, 0.36, 1] }}
          data-testid="hero-stats"
          className="mt-8 hidden flex-wrap items-center justify-center gap-3 rounded-full border border-white/10 bg-white/5 px-6 py-3 text-base text-fog md:flex"
        >
          <span>{t("hero.stats1", "09 Disciplines")}</span>
          <span className="text-white/20">·</span>
          <span>{t("hero.stats2", "Subliminal Scale")}</span>
          <span className="text-white/20">·</span>
          <span>{t("hero.stats3", "First Solutions ≤ 24 Mo")}</span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.5, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 flex flex-col items-center gap-3"
        >
          <Link
            to="/assessment?src=hero"
            data-testid="hero-assessment-cta"
            onClick={() => track("assessment_cta_click", { src: "hero" })}
            className="group mx-auto flex min-h-[56px] w-full max-w-[340px] items-center justify-center gap-2.5 rounded-full border border-cyan-300/30 bg-white/5 px-4 py-2.5 backdrop-blur transition-colors duration-300 hover:border-cyan-300/60 md:mx-0 md:min-h-0 md:w-auto md:max-w-none md:justify-start md:gap-4 md:py-2 md:pl-2 md:pr-6"
          >
            <SampleRadar className="h-8 w-8 shrink-0 md:h-24 md:w-24" variant="green" />
            <span className="flex flex-wrap items-center justify-center gap-2 text-[15px] font-semibold text-white [text-wrap:balance] md:text-base">
              Assess Your Lifespan Markers
              <ArrowRight className="hidden h-4 w-4 text-cyan-300 transition-transform duration-300 group-hover:translate-x-0.5 min-[361px]:block" />
            </span>
          </Link>
          <div className="mt-3 flex flex-wrap justify-center gap-1.5" data-testid="hero-chips">
            <span className="chip chip-cyan">4-5 min</span>
            <span className="chip chip-violet">No login</span>
            <span className="chip chip-green">Free</span>
            <span className="chip chip-amber">Beta</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.45, ease: [0.22, 1, 0.36, 1] }}
          className="relative mt-16 w-full max-w-5xl overflow-hidden rounded-3xl border border-white/10 shadow-[0_24px_80px_rgba(59,130,246,0.12)]"
        >
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster="https://images.pexels.com/videos/35967934/pexels-photo-35967934.jpeg?auto=compress&w=1260"
            className="absolute inset-0 h-full w-full object-cover brightness-[0.7]"
          >
            <source src="https://videos.pexels.com/video-files/35967934/15249530_1920_1080_30fps.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-t from-void via-transparent to-transparent" />
          <div className="relative flex h-[320px] items-end p-6 md:h-[460px] md:p-8">
            <span className="on-media flex items-center gap-2.5 rounded-full bg-black/50 px-5 py-2.5 text-base font-medium text-white/85 backdrop-blur">
              <span className="h-2 w-2 animate-pulse-slow rounded-full bg-cyan-300" />
              {t("hero.caption", "Subliminal-scale research in progress")}
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
